import { useMutation, useQuery, queryCache } from "react-query";

import { STALE_BILLING_MESSAGE } from "manager/pages/Properties/billing/billingDisplay";
import { HTTP_ERROR_CODES, NOTIFICATIONS } from "shared/config/constants";
import {
  bulkGoLivePropertyBilling,
  cancelScheduledPropertyBilling,
  changePropertyGoLive,
  getPropertiesPerf,
  getPropertyBillingHistory,
  goLivePropertyBilling,
  setPropertyBillableUnits,
  stopPropertyBilling,
} from "shared/lib/api/propertyApi";
import { openNotification } from "shared/utils/ui";

import { PROPERTY_QUERY_KEYS } from "./propertyQueries";

const GET_BILLING_PROPERTIES = "getBillingProperties";
const GET_PROPERTY_BILLING_HISTORY = "getPropertyBillingHistory";

export const BILLING_PROPERTY_QUERY_KEYS = Object.freeze({
  list: (params) => [GET_BILLING_PROPERTIES, params],
  history: (propertyId, params) => [
    GET_PROPERTY_BILLING_HISTORY,
    Number(propertyId),
    params?.page,
    params?.pageSize,
  ],
});

const billingErrorDetail = (error): string | undefined => {
  const fromError = error?.errors?.error;
  if (typeof fromError === "string" && fromError) return fromError;
  const detail = error?.errors?.detail;
  if (typeof detail === "string" && detail) return detail;
  const nonField = error?.errors?.nonFieldErrors;
  if (Array.isArray(nonField) && typeof nonField[0] === "string") {
    return nonField[0];
  }
  return undefined;
};

export const notifyBillingWriteError = (error, fallback: string) => {
  if (error?.statusCode === HTTP_ERROR_CODES.conflict) {
    openNotification(
      billingErrorDetail(error) || STALE_BILLING_MESSAGE,
      NOTIFICATIONS.error
    );
    return;
  }
  openNotification(billingErrorDetail(error) || fallback, NOTIFICATIONS.error);
};

const invalidateBillingLists = () => {
  queryCache.invalidateQueries(PROPERTY_QUERY_KEYS.getBillingProperties());
  queryCache.invalidateQueries(PROPERTY_QUERY_KEYS.getProperties());
  queryCache.invalidateQueries(GET_PROPERTY_BILLING_HISTORY);
};

// One server page of one section. Do not recurse `next`. #4315 pages
// onboarding,scheduled (or live,stopping) as one queryset so Ant's
// pageSize stays 5/10/5 and the pager count does not drift.
export const fetchBillingSectionProperties = (params) =>
  getPropertiesPerf({
    nameOrAddress: params.nameOrAddress || undefined,
    portfolioId: params.portfolioId || undefined,
    billingStatus: params.billingStatus || undefined,
    page: params.page || 1,
    pageSize: params.pageSize,
  });

export const useGetBillingProperties = ({ params }, config = {}) =>
  useQuery(
    BILLING_PROPERTY_QUERY_KEYS.list(params),
    () => fetchBillingSectionProperties(params),
    {
      onError: () =>
        openNotification("Failed to load the properties", NOTIFICATIONS.error),
      ...config,
    }
  );

export const useGetPropertyBillingHistory = (
  { propertyId, page, pageSize },
  config = {}
) =>
  useQuery(
    BILLING_PROPERTY_QUERY_KEYS.history(propertyId, { page, pageSize }),
    () => getPropertyBillingHistory(propertyId, { page, pageSize }),
    {
      enabled: Boolean(propertyId),
      onError: () =>
        openNotification("Failed to load billing history", NOTIFICATIONS.error),
      ...config,
    }
  );

type BillingWriteArgs = {
  propertyId: number;
  payload: Record<string, unknown>;
};

type BulkGoLiveArgs = {
  propertyIds: Array<number | string>;
  billingLiveAt?: string;
  billingLiveAtApproximate?: boolean;
  reason?: string;
  expected?: Array<Record<string, unknown>>;
};

const useBillingWrite = <TVariables>(
  writer: (variables: TVariables) => Promise<unknown>,
  fallback: string
) => {
  const [mutate, { isLoading }] = useMutation<unknown, unknown, TVariables>(
    writer,
    {
      throwOnError: true,
      onSuccess: () => invalidateBillingLists(),
      onError: (error) => notifyBillingWriteError(error, fallback),
    }
  );
  return { mutate, isLoading };
};

export const useGoLivePropertyBilling = () => {
  const { mutate, isLoading } = useBillingWrite<BillingWriteArgs>(
    ({ propertyId, payload }) => goLivePropertyBilling(propertyId, payload),
    "Failed to set the property live."
  );
  return { goLivePropertyBilling: mutate, isSaving: isLoading };
};

export const useChangePropertyGoLive = () => {
  const { mutate, isLoading } = useBillingWrite<BillingWriteArgs>(
    ({ propertyId, payload }) => changePropertyGoLive(propertyId, payload),
    "Failed to correct the go-live date."
  );
  return { changePropertyGoLive: mutate, isSaving: isLoading };
};

export const useStopPropertyBilling = () => {
  const { mutate, isLoading } = useBillingWrite<BillingWriteArgs>(
    ({ propertyId, payload }) => stopPropertyBilling(propertyId, payload),
    "Failed to stop billing."
  );
  return { stopPropertyBilling: mutate, isSaving: isLoading };
};

export const useCancelScheduledPropertyBilling = () => {
  const { mutate, isLoading } = useBillingWrite<BillingWriteArgs>(
    ({ propertyId, payload }) =>
      cancelScheduledPropertyBilling(propertyId, payload),
    "Failed to cancel the scheduled change."
  );
  return { cancelScheduledPropertyBilling: mutate, isSaving: isLoading };
};

export const useSetPropertyBillableUnits = () => {
  const { mutate, isLoading } = useBillingWrite<BillingWriteArgs>(
    ({ propertyId, payload }) => setPropertyBillableUnits(propertyId, payload),
    "Failed to update billable units."
  );
  return { setPropertyBillableUnits: mutate, isSaving: isLoading };
};

export const useBulkGoLivePropertyBilling = () => {
  const { mutate, isLoading } = useBillingWrite<BulkGoLiveArgs>(
    (payload) => bulkGoLivePropertyBilling(payload),
    "Failed to set properties live."
  );
  return { bulkGoLivePropertyBilling: mutate, isSaving: isLoading };
};
